import { BCLabel } from '@bimser/components';
import { RuleEngineEntities } from '@bimser/components/dist/BCRuleManager';
import { IDictionary, MLHelper, RxEventEmitter, isNullOrUndefined } from '@bimser/core';
import classNames from 'classnames/bind';
import * as React from 'react';
import {
	CovertFontToCSS,
	CovertSizeToCSS,
	ICSSProperties,
	MarkPosition
} from '../../../../entities/common';
import Styles from './assets/captionWrapperStyles.scss';
import { ICaptionWrapperProperties } from './entities/ICaptionWrapperProperties';

const cx = classNames.bind(Styles);
const highlightSignalKey = 'ACTIVATE_CONTROL_HIGHLIGHT';

const CaptionWrapper = (props: ICaptionWrapperProperties) => {
	const [highlight, setHighlight] = React.useState<{
		type: RuleEngineEntities.AlertType;
		validationMessage: IDictionary<string>;
	}>(null);

	React.useEffect(() => {
		if (props.windowId && props.controlName) {
			const subjectId = `highlight_${props.windowId}${props.controlName}${props.activeView}`;
			const subjectKey = RxEventEmitter.createName(subjectId);
			const highlight = RxEventEmitter.subjects[subjectKey];
			const uncaughtHighlight = RxEventEmitter.uncaughtSubjects[subjectKey];

			if (
				uncaughtHighlight?.type === highlightSignalKey &&
				uncaughtHighlight?.windowId === props.windowId
			) {
				setHighlight({
					type: uncaughtHighlight.alertType,
					validationMessage: uncaughtHighlight.messages
				});

				delete RxEventEmitter.uncaughtSubjects[subjectKey];
			} else if (!highlight) {
				RxEventEmitter.listen(subjectId, {
					next: data => {
						if (data.type === highlightSignalKey && data.windowId === props.windowId) {
							setHighlight({
								type: data.alertType,
								validationMessage: data.messages
							});
						}
					}
				});
			}
		}

		return () => {
			const subjectId = `$highlight_${props.windowId}${props.controlName}${props.activeView}`;

			if (RxEventEmitter.subjects[subjectId]) {
				RxEventEmitter.subjects[subjectId].unsubscribe();
				delete RxEventEmitter.subjects[subjectId];
			}
		};
	}, []);

	const renderCaption = (captionStyle: ICSSProperties) => {
		const { visible, ellipsis, text, horizontalAlign, font, showColon } = props.caption;

		if (visible) {
			const captionStyles: ICSSProperties = { ...CovertFontToCSS(font) };

			return (
				<div
					className={'Form_Control_Caption ' + Styles.captionWrapperCaption}
					style={captionStyle}
				>
					<BCLabel
						ellipsis={ellipsis}
						align={horizontalAlign}
						customStyles={captionStyles}
						title={renderTextAsTitle()}
					>
						{renderMark('atFirst')}
						{text.getText()}
						{renderMark('atLast')}
						{showColon ? ':' : undefined}
					</BCLabel>
				</div>
			);
		}
	};

	const renderTextAsTitle = () => {
		const { char, position } = props.caption.markSettings;
		if (isNullOrUndefined(char)) {
			return props.caption.text.getText();
		}
		if (position == 'atFirst') {
			return char + props.caption.text.getText();
		} else {
			return props.caption.text.getText() + char;
		}
	};

	const renderMark = (pos: MarkPosition) => {
		const { char, position, style } = props.caption.markSettings;
		if (char && pos === position) {
			return <span style={style}>{char}</span>;
		}
	};

	const getContainerClassNames = () => {
		const position = props.caption.position;
		const containerClassName = 'Form_Control_' + props.controlType;

		return cx({
			captionWrapper: true,
			positionTop: position === 'top',
			positionLeft: position === 'left',
			positionRight: position === 'right',
			[containerClassName]: true,
			[props.customClassName]: props.customClassName ? true : false
		});
	};

	const getStyles = () => {
		const { caption } = props;

		const captionStyle: ICSSProperties = {},
			controlWrapperStyle: ICSSProperties = {},
			containerStyle: ICSSProperties = { ...props.containerStyle };

		if (caption.size) {
			const { width, height } = CovertSizeToCSS(caption.size);

			captionStyle.height = height != '' ? height : 'auto';
			captionStyle.maxWidth = '100%';

			if (caption.position === 'top') {
				captionStyle.width = '100%';
			}

			if (caption.position === 'left' || caption.position === 'right') {
				captionStyle.flexBasis = width != '' ? width : '120px';
			}
		}

		if (caption.verticalAlign) {
			switch (caption.verticalAlign) {
				case 'top': {
					captionStyle.alignItems = 'flex-start';
					break;
				}
				case 'bottom': {
					captionStyle.alignItems = 'flex-end';
					break;
				}
				case 'middle':
				default: {
					captionStyle.alignItems = 'center';
					break;
				}
			}
		}

		return {
			captionStyle,
			controlWrapperStyle,
			containerStyle
		};
	};

	const onClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		e.persist();
		if (props.onClick) {
			props.onClick(e);
		}
		// e.stopPropagation(); // Form içeriğine tıklandığı zaman flexpanelin aktifliğini tetiklemesini engellediğinden dolayı kapatılmıştır.
	};

	const onDoubleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		e.persist();
		if (props.onDoubleClick) {
			props.onDoubleClick(e);
		}
		e.stopPropagation();
	};

	const onFocus = (e: any) => setHighlight(null);

	const getControlWrapperStyles = () =>
		cx({
			inputHighlight: highlight,
			successHighlight: highlight?.type === 'Success',
			errorHighlight: highlight?.type === 'Error',
			warningHighlight: highlight?.type === 'Warning',
			infoHighlight: highlight?.type === 'Info',
			blockedHighlight: highlight?.type === 'Blocked',
			severeWarningHighlight: highlight?.type === 'SevereWarning'
		});

	const getValidationMessage = () => {
		if (highlight?.validationMessage) {
			return (
				<span className="validationMessageStyle">
					{MLHelper.getMLText(highlight.validationMessage)}
				</span>
			);
		}
	};

	if (props.controlType === 'Form') {
		return <>{props.children}</>;
	}
	const { captionStyle, controlWrapperStyle, containerStyle } = getStyles();

	return (
		<div
			id={props.controlName}
			className={getContainerClassNames()}
			style={containerStyle ? { ...containerStyle } : {}}
			onClick={onClick}
			onDoubleClick={onDoubleClick}
		>
			{renderCaption(captionStyle)}
			<div
				onFocus={onFocus}
				className={
					'Form_Control ' + Styles.captionWrapperControl + ' ' + getControlWrapperStyles()
				}
				style={controlWrapperStyle}
				title={props.title?.getText()}
			>
				{props.children}
				{getValidationMessage()}
			</div>
		</div>
	);
};

export default CaptionWrapper;
